import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deploymentNoticeApi } from '@/api/services/deploymentNotice';
import type {
  DeployNoticeInput,
  DeploymentNoticeTemplateInput,
} from '@/api/types/deploymentNotice';

export const deploymentNoticeKeys = {
  all: ['deploymentNotice'] as const,
  status: ['deploymentNotice', 'status'] as const,
  styleOptions: ['deploymentNotice', 'styleOptions'] as const,
  tenants: ['deploymentNotice', 'tenants'] as const,
  templates: ['deploymentNotice', 'templates'] as const,
};

export function useNoticeStatus() {
  return useQuery({
    queryKey: deploymentNoticeKeys.status,
    queryFn: () => deploymentNoticeApi.getStatus(),
    // Mirrors the legacy countdown refresh.
    refetchInterval: 15000,
  });
}

export function useStyleOptions() {
  return useQuery({
    queryKey: deploymentNoticeKeys.styleOptions,
    queryFn: () => deploymentNoticeApi.getStyleOptions(),
  });
}

export function useNoticeTenants() {
  return useQuery({
    queryKey: deploymentNoticeKeys.tenants,
    queryFn: () => deploymentNoticeApi.getTenants(),
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: deploymentNoticeKeys.templates,
    queryFn: () => deploymentNoticeApi.getTemplates(),
  });
}

export function useDeployNotice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DeployNoticeInput) => deploymentNoticeApi.deploy(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: deploymentNoticeKeys.status });
      qc.invalidateQueries({ queryKey: deploymentNoticeKeys.templates });
    },
  });
}

export function useClearNotice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noticeId: string) => deploymentNoticeApi.clear(noticeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: deploymentNoticeKeys.status }),
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DeploymentNoticeTemplateInput) =>
      deploymentNoticeApi.createTemplate(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: deploymentNoticeKeys.templates }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deploymentNoticeApi.deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: deploymentNoticeKeys.templates }),
  });
}
